import React, { useEffect, useState } from 'react';
import styles from './Instruction.module.scss';
import { InstructionDialog } from '../../shared/VideoDialog';
import VideoPreviewDialog from '../../shared/VideoPreviewDialog';
import VideoCard from '../../shared/VideoCard';
import Loader from '../../shared/Loader';
import { useTranslation } from 'react-i18next';
import { AddIcon } from '../../icons';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { useInstruction } from '../../../redux/hooks';
import { useVideoProcessingStatus } from '../../../hooks/useVideoProcessingStatus';
import type { ITraining } from '../../../types';

const Instruction: React.FC = () => {
    const { t } = useTranslation();
    const [newVideoIds, setNewVideoIds] = useState<Set<number>>(new Set());

    const {
        selectedVideo,
        isLoading,
        deleteDialog,
        openDialog,
        video,
        hasVideo,
        loadInstructionVideos,
        handleSubmit,
        handleDelete,
        confirmDelete,
        handleVideoClick,
        closeVideoPreview,
        handleOpenDialog,
        closeDialog,
        closeDeleteDialog,
        updateVideoFromStatus
    } = useInstruction();

    // Хук для отслеживания статуса обработки видео (если есть видео)
    const { status: processingStatus, message: processingMessage } = useVideoProcessingStatus({
        videoId: video?.id || 0,
        autoRefresh: !!video?.id,
        refreshInterval: 3000,
        onStatusChange: (status, data) => {
            // Обновляем видео в Redux при получении новых данных
            if (video && updateVideoFromStatus && data) {
                const updates: Partial<ITraining> = {};

                // Обновляем video_url если получили
                if (data.url && data.url !== (typeof video.video_url === 'string' ? video.video_url : '')) {
                    (updates as any).video_url = data.url;
                }

                // Обновляем preview_url если получили
                if (data.preview_url && data.preview_url !== (typeof video.preview_url === 'string' ? video.preview_url : '')) {
                    (updates as any).preview_url = data.preview_url;
                }

                // Обновляем title если получили
                if (data.title && data.title !== video.title) {
                    updates.title = data.title;
                }

                // Если есть обновления, применяем их
                if (Object.keys(updates).length > 0) {
                    updateVideoFromStatus(video.id, updates);

                    // Если получили video_url или preview_url, убираем видео из списка новых
                    if ((updates.video_url || updates.preview_url)) {
                        setNewVideoIds(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(video.id);
                            return newSet;
                        });
                    }
                }
            }
        }
    });

    // Обертка для handleSubmit с отслеживанием новых видео
    const handleVideoSubmit = async (data: any) => {
        try {
            const result = await handleSubmit(data);

            // Если добавление прошло успешно и мы получили ID нового видео
            if (result && typeof result === 'object' && 'type' in result && result.type.endsWith('/fulfilled') && 'payload' in result && result.payload && typeof result.payload === 'object' && 'id' in result.payload) {
                const videoId = (result.payload as any).id;
                setNewVideoIds(prev => new Set(prev).add(videoId));

                // Убираем ID из списка новых через некоторое время
                setTimeout(() => {
                    setNewVideoIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                }, 30000); // 30 секунд на обработку
            }
        } catch (error) {
            console.error('Ошибка при добавлении видео инструкции:', error);
        }
    };

    useEffect(() => {
        loadInstructionVideos();
    }, [loadInstructionVideos]);

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>
                    {t('content.instruction.title')}
                </h1>
                <div className={styles.buttonGroup}>
                    <button
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={handleOpenDialog}
                    >
                        <AddIcon /> {hasVideo ? t('content.instruction.changeButton') : t('content.instruction.addButton')}
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className={styles.emptyState}>
                    <Loader fullScreen={false} />
                </div>
            ) : hasVideo ? (
                <div className={styles.videoContainer}>
                    <VideoCard
                        key={video!.id}
                        id={video!.id}
                        title={video!.title}
                        thumbnailUrl={typeof video!.preview_url === 'string' ? video!.preview_url : ''}
                        videoUrl={typeof video!.video_url === 'string' ? video!.video_url : ''}
                        processingStatus={processingStatus}
                        processingMessage={processingMessage}
                        onEdit={handleOpenDialog}
                        onDelete={() => handleDelete(video!.id)}
                        onClick={() => handleVideoClick(video!)}
                        horizontal={true}
                        hideTitle={true}
                        showProcessingIndicator={processingStatus !== 'completed'}
                        isNewVideo={newVideoIds.has(video!.id)}
                    />
                    {video!.description && (
                        <div className={styles.descriptionContainer}>
                            <h3 className={styles.descriptionTitle}>{t('common.description')}</h3>
                            <p className={styles.descriptionText}>{video!.description}</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <p className={styles.emptyStateText}>{t('content.instruction.noVideo')}</p>
                </div>
            )}

            <InstructionDialog
                open={openDialog}
                onClose={closeDialog}
                onSubmit={handleVideoSubmit}
                dialogTitle={t('content.instruction.uploadDialog.title')}
                initialData={video ? {
                    id: video.id,
                    title: video.title,
                    description: video.description || '',
                    videoType: video.video_type as 'youtube' | 'file',
                    youtubeUrl: video.youtube_url,
                    language: video.language as 'ru' | 'it',
                } : undefined}
            />

            <VideoPreviewDialog
                open={!!selectedVideo}
                onClose={closeVideoPreview}
                title={selectedVideo?.title || ''}
                videoUrl={selectedVideo?.youtube_url || (typeof selectedVideo?.video_url === 'string' ? selectedVideo.video_url : '')}
                isYouTube={selectedVideo?.video_type === 'youtube'}
            />

            <ConfirmDialog
                open={deleteDialog.open}
                title={t('content.instruction.deleteDialog.title')}
                message={t('content.instruction.deleteDialog.message')}
                onConfirm={confirmDelete}
                onCancel={closeDeleteDialog}
            />
        </div>
    );
};

export default Instruction;